/**
 * Arabic catalogue. This is the source language: every key exists here first.
 *
 * Feature branches add their own namespace file (`patients.ar.ts`,
 * `appointments.ar.ts`, ...) and spread it in below. That way an epic owns its
 * strings and merging two epics is a two-line diff, not a conflict inside one
 * 800-key object.
 */
import { authAr } from './auth.ar';
import { patientsAr } from './patients.ar';
import { flowAr } from './flow.ar';
import { appointmentsAr } from './appointments.ar';
import { platformAr } from './platform.ar';
import { diagnosticCasesAr } from './diagnosticCases.ar';

export const ar: Record<string, string> = {
  // EPIC AUTH owns its strings in auth.ar.ts.
  ...authAr,
  // EPIC PAT owns its strings in patients.ar.ts.
  ...patientsAr,
  // EPIC FLOW owns its strings in flow.ar.ts.
  ...flowAr,
  // EPIC APT owns its strings in appointments.ar.ts.
  ...appointmentsAr,

  // EPIC DXC owns its strings in diagnosticCases.ar.ts.
  ...diagnosticCasesAr,

  // EPIC PLAT owns its strings in platform.ar.ts.
  ...platformAr,

  // --- Application chrome --------------------------------------------------
  'app.name': 'Clinvisor',
  'app.search.placeholder': 'ابحث عن مريض، موعد، فاتورة…',
  'app.search.scopeNote': 'النتائج مقيّدة بصلاحياتك وفرعك النشط',
  'app.allScreens': 'كل الشاشات',
  'app.activeBranch': 'الفرع النشط',
  'app.signOut': 'تسجيل الخروج',
  'app.notifications': 'مركز الإشعارات',
  'app.notifications.all': 'الكل',
  'app.notifications.critical': 'حرجة',
  'app.notifications.finance': 'مالية',
  'app.notifications.empty': 'لا توجد إشعارات',
  'app.skipToContent': 'تخطَّ إلى المحتوى',
  'app.loading': 'جارٍ التحميل…',

  // --- Navigation ----------------------------------------------------------
  'nav.dashReception': 'لوحة اليوم',
  'nav.dashDoctor': 'لوحة الطبيب',
  'nav.appointments': 'المواعيد',
  'nav.waiting': 'غرفة الانتظار',
  'nav.patients': 'المرضى',
  'nav.visit': 'مركز الزيارة',
  'nav.lab': 'المختبر',
  'nav.imaging': 'الأشعة',
  'nav.pharmacy': 'الصيدلية',
  'nav.checkout': 'المحاسبة',
  'nav.invoices': 'الفواتير',
  'nav.expenses': 'المصروفات',
  'nav.reports': 'التقارير',
  'nav.users': 'المستخدمون',
  'nav.doctors': 'الأطباء والجداول',
  'nav.branches': 'الفروع',
  'nav.services': 'الخدمات والأسعار',
  'nav.reference': 'المرجع الطبي',
  'nav.ai': 'المساعد الذكي',
  'nav.onboarding': 'قائمة التهيئة',
  'nav.settings': 'الإعدادات',
  'nav.subscription': 'الاشتراك والباقة',
  'nav.audit': 'سجل التدقيق',
  'nav.platformTenants': 'العيادات المشتركة',
  'nav.platformHealth': 'صحة المنصة',
  'nav.platformLeads': 'طلبات العروض',
  'nav.plans': 'الباقات والحدود',
  'nav.platformAudit': 'تدقيق المنصة',

  // --- Roles ---------------------------------------------------------------
  'role.OWNER': 'مالك العيادة',
  'role.ADMIN': 'مدير العيادة',
  'role.DOCTOR': 'طبيب',
  'role.RECEPTION': 'استقبال',
  'role.ACCOUNTING': 'محاسبة',
  'role.LAB': 'مختبر',
  'role.RADIOLOGY': 'أشعة',
  'role.PHARMACY': 'صيدلية',
  'role.MEMBER': 'مستخدم',
  'role.platform': 'مشرف المنصة',

  // --- Common actions ------------------------------------------------------
  'action.save': 'حفظ',
  'action.cancel': 'إلغاء',
  'action.confirm': 'تأكيد',
  'action.close': 'إغلاق',
  'action.retry': 'إعادة المحاولة',
  'action.edit': 'تعديل',
  'action.delete': 'حذف',
  'action.export': 'تصدير',
  'action.print': 'طباعة',
  'action.search': 'بحث',
  'action.filter': 'تصفية',
  'action.back': 'رجوع',
  'action.next': 'التالي',
  'action.add': 'إضافة',

  // --- Shared states -------------------------------------------------------
  'state.empty': 'لا توجد بيانات',
  'state.error': 'تعذّر تحميل البيانات',
  'state.errorBody': 'حدث خطأ غير متوقع. يمكنك إعادة المحاولة.',
  'state.forbidden': 'غير مصرّح',
  'state.forbiddenBody': 'ليس لديك صلاحية لعرض هذه الشاشة.',
  'state.correlationId': 'معرّف الطلب',
  'state.conflict': 'عدّل مستخدم آخر هذا السجل بعد أن فتحته.',
  'state.subscriptionBlocked': 'هذه الميزة غير متاحة في باقتك',
  'state.upgrade': 'ترقية الباقة',
  'state.offline': 'التحديث الفوري غير متاح — يتم التحديث كل ٣٠ ثانية',

  // --- Appointment statuses ------------------------------------------------
  'appointmentStatus.REQUESTED': 'بانتظار التأكيد',
  'appointmentStatus.CONFIRMED': 'مؤكد',
  'appointmentStatus.ARRIVED': 'وصل',
  'appointmentStatus.IN_PROGRESS': 'داخل العيادة',
  'appointmentStatus.READY_CHECKOUT': 'جاهز للمحاسبة',
  'appointmentStatus.COMPLETED': 'مكتمل',
  'appointmentStatus.CANCELLED': 'ملغى',
  'appointmentStatus.NO_SHOW': 'لم يحضر',

  // --- Visit statuses ------------------------------------------------------
  'visitStatus.DRAFT': 'مسودة',
  'visitStatus.FINALISED': 'مثبتة',
  'visitStatus.AMENDED': 'معدّلة',
  'visitStatus.VOIDED': 'ملغاة',

  // --- Invoice statuses ----------------------------------------------------
  'invoiceStatus.DRAFT': 'مسودة',
  'invoiceStatus.ISSUED': 'صادرة',
  'invoiceStatus.PARTIAL': 'مدفوعة جزئياً',
  'invoiceStatus.PAID': 'مدفوعة',
  'invoiceStatus.VOID': 'ملغاة',
  'invoiceStatus.REFUNDED': 'مستردة',

  // --- Impersonation banner ------------------------------------------------
  'impersonation.title': 'وضع دعم المنصة — تعمل داخل مستأجر «{tenant}»',
  'impersonation.purpose': 'الغرض: {purpose}',
  'impersonation.end': 'إنهاء الجلسة',

  // --- Coded terminology (BUG-05) -------------------------------------------
  'term.label': 'التشخيص',
  'term.placeholder': 'ابحث بالاسم أو الرمز…',
  'term.placeholderFreeText': 'اكتب التشخيص…',
  'term.results': 'نتائج البحث',
  'term.searching': 'جارٍ البحث…',
  'term.searchFailed': 'تعذّر البحث في المرجع. يمكنك كتابة التشخيص نصاً.',
  'term.useFreeText': 'استخدام النص كما هو:',
  'term.useFreeTextHint': 'يُحفظ دون رمز تشخيصي.',
  'term.uncoded': 'غير مرمّز',
  'term.clear': 'مسح الاختيار',
  'term.clinicAlias': 'مرادف',
  'term.catalogueEmpty': 'لم يتم استيراد مرجع التشخيصات بعد، لذا تُحفظ التشخيصات نصاً دون رمز.',
};
